import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import "./Contact.css";


export default function Contact(): JSX.Element {
  const [formState, setFormState] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });
  const [submitStatus, setSubmitStatus] = useState<"idle" | "sending" | "success" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    const params = new URLSearchParams(window.location.search);
    const reason = params.get("reason");
    if (reason === "wholesale-application") {
      setFormState(prev => ({
        ...prev,
        subject: prev.subject || "Wholesale application",
      }));
    }
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormState(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitStatus === "sending") return;
    if (![formState.name, formState.email, formState.message, formState.subject].every(value => value.trim())) {
      setErrorMessage("Please enter your name, email address, subject and message. Fields cannot contain only spaces.");
      setSubmitStatus("error");
      return;
    }
    setErrorMessage("");
    setSubmitStatus("sending");
    try {
      const payload = {
        source: "Contact",
        name: formState.name,
        email: formState.email,
        phone: formState.phone,
        subject: formState.subject || "Contact request",
        message: formState.message,
      };
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const result = await response.json();
        throw new Error(result.error || "Unable to send your enquiry. Please try again.");
      }
      setSubmitStatus("success");
      setFormState({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      console.error("Contact form submit error:", err);
      setErrorMessage(err instanceof Error ? err.message : "Unable to send your enquiry. Please try again.");
      setSubmitStatus("error");
    }
  };

  return (
    <motion.main
      className="page contact-page"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
    >
      <div className="contact-hero">
        <h1>Contact Us</h1>
        <p>Have questions or need help? Fill out the form or reach us directly below.</p>
      </div>

      <div className="contact-wrapper">
        <div className="contact-info">
          <h2>Reach Us</h2>
          <ul>
            <li>
              <strong>Email:</strong> info@jortel.com.au
            </li>
            <li>
              <strong>Phone:</strong> 1300 JORTEL (567 835)
            </li>
            <li>
              <strong>Address:</strong> 7B/256 New Line Rd, Dural NSW 2158
            </li>
          </ul>

          <div className="support-links">
            <a href="/faq">Help Articles &amp; FAQ</a>
          </div>
        </div>

        <div className="contact-form-wrapper">
          <motion.form
            className="contact-form"
            onSubmit={handleSubmit}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <div className="form-group">
              <label htmlFor="name">Full Name</label>
              <input
                id="name"
                name="name"
                type="text"
                value={formState.name}
                onChange={handleChange}
                placeholder="Your full name"
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="email">Email Address</label>
              <input
                id="email"
                name="email"
                type="email"
                value={formState.email}
                onChange={handleChange}
                placeholder="you@example.com"
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="phone">Phone Number</label>
              <input
                id="phone"
                name="phone"
                type="tel"
                value={formState.phone}
                onChange={handleChange}
                placeholder="0400 123 456"
              />
            </div>
            <div className="form-group">
              <label htmlFor="subject">Subject</label>
              <input
                id="subject"
                name="subject"
                type="text"
                value={formState.subject}
                onChange={handleChange}
                placeholder="Subject"
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="message">Message</label>
              <textarea
                id="message"
                name="message"
                rows={5}
                value={formState.message}
                onChange={handleChange}
                placeholder="How can we help?"
                required
              />
            </div>

            <button type="submit" className="btn primary" disabled={submitStatus === "sending"}>
              {submitStatus === "sending" ? "Sending..." : "Send Message"}
            </button>

            {submitStatus === "success" && <p className="form-feedback success">Thank you - we'll be in touch soon.</p>}
            {submitStatus === "error" && <p className="form-feedback error" role="alert">{errorMessage}</p>}
          </motion.form>
        </div>
      </div>
    </motion.main>
  );
}


